import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  TextField,
  Typography,
  Checkbox,
  IconButton,
  Container
} from '@material-ui/core';
import {
  CheckCircleRounded as ValidIcon,
  CancelRounded as InvalidIcon,
  CloseRounded as CloseIcon
} from '@material-ui/icons';
import styled from 'styled-components';

import { baseUrl, useDebounce, validateEmail } from 'framework';
import { Line, Spacer, TitleContainer } from 'components';
import { TermsOfService, PrivacyPolicy } from './shared';
import { suggestOrgIdFromEmail, suggestOrgIdFromName } from './checkout-org-id';
import {
  clearSignupPlatformToken,
  consumeSignupPrefill,
  getSignupPlatformToken,
  SignupPrefill
} from './signup-prefill';

const ValidIconStyled = styled(ValidIcon)`
  color: ${p => p.theme.palette.success.main};
`;
const InvalidIconStyled = styled(InvalidIcon)`
  color: ${p => p.theme.palette.error.main};
`;

const CentralPanel = styled(Card)`
  padding: ${p => p.theme.spacing(2, 2)};
  max-width: 640px;
  margin: auto;
`;

const TosContainer = styled(Container)`
  padding: ${p => p.theme.spacing(4)}px;
  background-color: ${p => p.theme.palette.background.paper};
`;

const Row = styled(Box)`
  display: flex;
  margin: ${p => p.theme.spacing(2, 2)};
  flex-direction: row;
  align-items: center;
`;

const GoogleSignupButton = styled(Button)`
  background: #ffffff;
  color: #3c4043;
  border: 1px solid #dadce0;
  border-radius: 999px;
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.16), 0 1px 3px rgba(60, 64, 67, 0.24);
  padding: ${p => p.theme.spacing(1.5, 2.5)};
  font-weight: 700;
  letter-spacing: 0.01em;
  text-transform: none;

  &:hover {
    background: #f8fbff;
    box-shadow: 0 2px 4px rgba(60, 64, 67, 0.18),
      0 3px 8px rgba(60, 64, 67, 0.2);
  }

  &:focus {
    box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.24),
      0 2px 4px rgba(60, 64, 67, 0.18), 0 3px 8px rgba(60, 64, 67, 0.2);
  }

  &:disabled {
    background: #f7f8f8;
    color: rgba(60, 64, 67, 0.55);
    border-color: #e0e3e7;
  }

  & .MuiButton-label {
    justify-content: center;
  }

  & .MuiButton-startIcon {
    margin-left: 0;
    margin-right: ${p => p.theme.spacing(1.5)};
  }

  width: 100%;
`;

const CheckoutButton = styled(Button)`
  min-width: 220px;
  white-space: nowrap;
`;

const PasswordSignupRevealRow = styled(Box)`
  margin: ${p => p.theme.spacing(1)}px auto 0;
  display: flex;
  justify-content: center;
`;

const PasswordSignupRevealButton = styled(Button)`
  color: ${p => p.theme.palette.text.secondary};
  font-weight: 700;
  letter-spacing: 0.01em;
  text-transform: none;
`;

const PasswordSignupPanel = styled(Box)`
  margin-top: ${p => p.theme.spacing(3)}px;
  padding: ${p => p.theme.spacing(3)}px;
  border: 1px solid ${p => p.theme.palette.divider};
  border-radius: 20px;
  background: linear-gradient(180deg, #fbfdff 0%, #f3f6fb 100%);
`;

const TosPrivacyText = styled(Typography)`
  cursor: pointer;
  user-select: none;
`;

const TextLink = styled(Typography)`
  margin-left: 0.33em;
  margin-right: 0.33em;
  text-decoration: underline;
  color: ${p => p.theme.palette.primary.main};
  cursor: pointer;
  user-select: none;
`;

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M17.64 9.205c0-.639-.057-1.251-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615Z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.258c-.807.541-1.84.861-3.048.861-2.344 0-4.328-1.583-5.036-3.709H.957v2.332A9 9 0 0 0 9 18Z"
    />
    <path
      fill="#FBBC05"
      d="M3.964 10.714A5.41 5.41 0 0 1 3.682 9c0-.595.102-1.173.282-1.714V4.954H.957A9 9 0 0 0 0 9c0 1.452.348 2.827.957 4.046l3.007-2.332Z"
    />
    <path
      fill="#EA4335"
      d="M9 3.577c1.321 0 2.507.455 3.44 1.348l2.581-2.581C13.463.891 11.426 0 9 0A9 9 0 0 0 .957 4.954l3.007 2.332C4.672 5.16 6.656 3.577 9 3.577Z"
    />
  </svg>
);

const MicrosoftMark = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path fill="#f25022" d="M1 1h7.6v7.6H1z" />
    <path fill="#7fba00" d="M9.4 1H17v7.6H9.4z" />
    <path fill="#00a4ef" d="M1 9.4h7.6V17H1z" />
    <path fill="#ffb900" d="M9.4 9.4H17V17H9.4z" />
  </svg>
);

interface State {
  orgName?: string;
  orgId?: string;
  email?: string;
}

interface CheckoutLocationState {
  signupPrefill?: SignupPrefill;
}

export function Checkout() {
  const { t } = useTranslation();
  const { product } = useParams<{ product: string }>();
  const location = useLocation<CheckoutLocationState>();
  const hasStoredSignupPlatformToken = !!getSignupPlatformToken();

  const [signupPrefill] = useState<SignupPrefill>(() => ({
    ...consumeSignupPrefill(),
    ...(location.state?.signupPrefill || {})
  }));
  const initialSuggestedOrgId =
    signupPrefill.orgId ||
    suggestOrgIdFromName(signupPrefill.orgName) ||
    suggestOrgIdFromEmail(signupPrefill.email);

  const [state, setState] = useState<State>(() => ({
    ...(signupPrefill.orgName ? { orgName: signupPrefill.orgName } : {}),
    ...(signupPrefill.email ? { email: signupPrefill.email } : {}),
    ...(initialSuggestedOrgId ? { orgId: initialSuggestedOrgId } : {})
  }));
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [acceptedTos, setAcceptedTos] = useState(false);
  const [tosVisible, setTosVisible] = useState(false);
  const [privacyVisible, setPrivacyVisible] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lastSuggestedOrgId, setLastSuggestedOrgId] = useState(
    initialSuggestedOrgId
  );
  const [showPasswordSignup, setShowPasswordSignup] = useState(
    !!signupPrefill.email ||
      !!signupPrefill.orgName ||
      !!signupPrefill.orgId ||
      hasStoredSignupPlatformToken
  );

  const platformGoogleSignup = () => {
    const queryParams = new URLSearchParams({
      intent: 'signup',
      returnTo: `/checkout/${product}`
    });
    window.location.href = `${baseUrl}/auth/google/start?${queryParams.toString()}`;
  };

  const platformMicrosoftSignup = () => {
    const queryParams = new URLSearchParams({
      intent: 'signup',
      returnTo: `/checkout/${product}`
    });
    window.location.href = `${baseUrl}/auth/microsoft/start?${queryParams.toString()}`;
  };

  const checkOrg = async (orgId: string) => {
    // const { orgId } = state;
    if (!orgId || orgId.length <= 3) {
      setValid(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    const response = await fetch(`${baseUrl}/checkOrg/${orgId}`);
    if (response.status === 404) {
      setValid(true);
    } else {
      setValid(false);
    }
    setLoading(false);
  };

  const debouncedCheckOrg = useDebounce(checkOrg, 500);

  useEffect(() => {
    if (initialSuggestedOrgId) {
      debouncedCheckOrg(initialSuggestedOrgId);
    }
  }, [debouncedCheckOrg, initialSuggestedOrgId]);

  const checkout = async (e: React.MouseEvent) => {
    e.preventDefault();
    const { email, orgId, orgName } = state;
    if (!email || !orgId || !orgName?.trim()) {
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const shouldUseSignupPlatformToken =
        !!signupPrefill.email &&
        signupPrefill.email.trim().toLowerCase() === email.trim().toLowerCase();
      const signupPlatformToken = shouldUseSignupPlatformToken
        ? getSignupPlatformToken()
        : null;
      const response = await fetch(`${baseUrl}/payment/trial/start`, {
        method: 'post',
        body: JSON.stringify({
          orgId,
          orgName: orgName.trim(),
          email,
          product
        }),
        headers: {
          'content-type': 'application/json',
          ...(signupPlatformToken
            ? { Authorization: `Bearer ${signupPlatformToken}` }
            : {})
        }
      });

      if (!response.ok) {
        setSubmitError(t('trialSignupFailed'));
        return;
      }

      const body = (await response.json()) as { token?: string };

      clearSignupPlatformToken();

      if (body.token) {
        const callbackUrl = `${
          window.location.origin
        }/callback/${orgId}?token=${encodeURIComponent(body.token)}`;
        window.location.href = callbackUrl;
        return;
      }

      window.location.href = `${window.location.origin}/${orgId}`;
    } catch {
      setSubmitError(t('trialSignupFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderIcon = (v: boolean) =>
    v ? <ValidIconStyled /> : <InvalidIconStyled />;

  if (privacyVisible) {
    return (
      <Box mt={4}>
        <Row>
          <Spacer />
          <IconButton onClick={() => setPrivacyVisible(false)}>
            <CloseIcon />
          </IconButton>
        </Row>
        <TosContainer>
          <PrivacyPolicy />
        </TosContainer>
      </Box>
    );
  }
  if (tosVisible) {
    return (
      <Box mt={4}>
        <Row>
          <Spacer />
          <IconButton onClick={() => setTosVisible(false)}>
            <CloseIcon />
          </IconButton>
        </Row>
        <TosContainer>
          <TermsOfService />
        </TosContainer>
      </Box>
    );
  }

  return (
    <Box mt={4}>
      <form>
        <CentralPanel>
          <TitleContainer>
            <Typography variant="h4" align="center">
              {t('createOrg')}
            </Typography>
          </TitleContainer>
          <Row />
          <Line />
          <Row />
          <Row>
            <GoogleSignupButton
              disableElevation
              size="large"
              onClick={platformGoogleSignup}
              startIcon={<GoogleMark />}
            >
              {t('signUpWithGoogle')}
            </GoogleSignupButton>
          </Row>
          <Row>
            <GoogleSignupButton
              disableElevation
              size="large"
              onClick={platformMicrosoftSignup}
              startIcon={<MicrosoftMark />}
            >
              {t('signUpWithMicrosoft')}
            </GoogleSignupButton>
          </Row>
          {!showPasswordSignup ? (
            <PasswordSignupRevealRow>
              <PasswordSignupRevealButton
                onClick={() => setShowPasswordSignup(true)}
              >
                {t('signUpWithPassword')}
              </PasswordSignupRevealButton>
            </PasswordSignupRevealRow>
          ) : null}
          {showPasswordSignup ? (
            <PasswordSignupPanel>
              <Row mt={0} mb={0}>
                <Typography variant="subtitle2">
                  {t('signUpWithPassword')}
                </Typography>
              </Row>
              <Row />
              <Row>
                <Typography variant="body2">{t('orgNameDesc')}</Typography>
              </Row>
              <Row>
                <TextField
                  label={t('orgName')}
                  value={state.orgName || ''}
                  fullWidth
                  variant="outlined"
                  size="medium"
                  onChange={(event: React.ChangeEvent<{ value: string }>) => {
                    const nextOrgName = event.target.value;
                    const nextSuggestedOrgId =
                      suggestOrgIdFromName(nextOrgName) ||
                      suggestOrgIdFromEmail(state.email);
                    const shouldAutofillOrgId =
                      !state.orgId || state.orgId === lastSuggestedOrgId;
                    const nextOrgId = shouldAutofillOrgId
                      ? nextSuggestedOrgId
                      : state.orgId;

                    setLastSuggestedOrgId(nextSuggestedOrgId);
                    setState({
                      ...state,
                      orgName: nextOrgName,
                      orgId: nextOrgId
                    });

                    if (shouldAutofillOrgId) {
                      debouncedCheckOrg(nextOrgId || '');
                    }
                  }}
                />
              </Row>
              <Row />
              <Row>
                <Typography variant="body2">{t('orgEmailDesc')}</Typography>
              </Row>
              <Row>
                <TextField
                  label={t('orgEmail')}
                  value={state.email || ''}
                  fullWidth
                  variant="outlined"
                  size="medium"
                  InputProps={{
                    endAdornment: renderIcon(validateEmail(state.email))
                  }}
                  onChange={(event: React.ChangeEvent<{ value: string }>) => {
                    const nextEmail = event.target.value;
                    const nextSuggestedOrgId =
                      suggestOrgIdFromEmail(nextEmail) ||
                      suggestOrgIdFromName(state.orgName);
                    const shouldAutofillOrgId =
                      !state.orgId || state.orgId === lastSuggestedOrgId;
                    const nextOrgId = shouldAutofillOrgId
                      ? nextSuggestedOrgId
                      : state.orgId;

                    setLastSuggestedOrgId(nextSuggestedOrgId);
                    setState({
                      ...state,
                      email: nextEmail,
                      orgId: nextOrgId
                    });

                    if (shouldAutofillOrgId) {
                      debouncedCheckOrg(nextOrgId || '');
                    }
                  }}
                />
              </Row>
              <Row />
              <Row>
                <Typography variant="body2">{t('orgIdDesc')}</Typography>
              </Row>
              <Row>
                <TextField
                  label={t('orgId')}
                  value={state.orgId || ''}
                  fullWidth
                  variant="outlined"
                  size="medium"
                  InputLabelProps={{
                    shrink: !!state.orgId
                  }}
                  InputProps={{
                    endAdornment: loading ? (
                      <CircularProgress size={24} />
                    ) : (
                      renderIcon(valid)
                    )
                  }}
                  onChange={(event: React.ChangeEvent<{ value: string }>) => {
                    const newOrgId = event.target.value.toLocaleLowerCase();
                    setState({
                      ...state,
                      orgId: newOrgId
                    });
                    debouncedCheckOrg(newOrgId);
                  }}
                />
              </Row>
              <Row />
              <Row>
                <Checkbox
                  checked={acceptedTos}
                  onChange={(e: React.ChangeEvent<{ checked: boolean }>) => {
                    setAcceptedTos(e.target.checked);
                  }}
                />
                <TosPrivacyText
                  onClick={() => setAcceptedTos(!acceptedTos)}
                  variant="body2"
                >
                  {t('agreeToTos1')}
                </TosPrivacyText>
                <TextLink onClick={() => setTosVisible(true)} variant="body2">
                  {t('agreeToTos2')}
                </TextLink>
                <TosPrivacyText
                  onClick={() => setAcceptedTos(!acceptedTos)}
                  variant="body2"
                >
                  {t('agreeToTos3')}
                </TosPrivacyText>
                <TextLink
                  onClick={() => setPrivacyVisible(true)}
                  variant="body2"
                >
                  {t('agreeToTos4')}
                </TextLink>
              </Row>
              <Row>
                <Spacer />
                <CheckoutButton
                  size="large"
                  variant="contained"
                  color="secondary"
                  disabled={
                    !state.orgName?.trim() ||
                    !valid ||
                    !validateEmail(state.email) ||
                    !acceptedTos ||
                    submitting
                  }
                  onClick={checkout}
                  type="submit"
                >
                  {submitting ? t('loading') : t('signUpWithPassword')}
                </CheckoutButton>
              </Row>
              {submitError ? (
                <Row>
                  <Typography color="error" variant="body2">
                    {submitError}
                  </Typography>
                </Row>
              ) : null}
            </PasswordSignupPanel>
          ) : null}
        </CentralPanel>
      </form>
    </Box>
  );
}
